import React, {
  FC, useCallback, useMemo, useState,
} from 'react';
import classNames from 'classnames';

import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import useInView from 'src/common/hooks/useInView';
import { Photo } from '../../types/api/v4/boats/search';
import getClassNames from '../../utils/getClassNames';
import ImgFromSrcSet, { ImgFromSrcSetProps } from '../ImgFromSrcSet';
import Carousel from '../Carousel';
import UniversalLink from '../UniversalLink';

import styles from './ListingCardImage.module.scss';

const urlToKey = (urlString: string) => {
  const url = new URL(urlString);
  return url.pathname;
};

const filterUnique = (photos: Photo[]) => photos.filter(
  (value, index, self) => self.findIndex((v) => v.url === value.url) === index,
);

const srcSet: ImgFromSrcSetProps['srcSet'] = [
  [1130, 426, 276],
  [950, 350, 227],
  [551, 445, 288],
  [420, 510, 330],
  [0, 378, 245],
];

const popupSrcSet: ImgFromSrcSetProps['srcSet'] = [[0, 120, 180]];

export type ListingCardImageProps = {
  src: string;
  alt: string;
  photos?: Photo[];
  loading?: 'lazy' | 'eager';
  fetchpriority?: 'high' | 'low';
  withPlaceholder?: boolean;
  isPopup?: boolean;
  isPreview?: boolean;
  link?: {
    url?: string;
    heading?: string;
    testMark?: string;
    target?: string;
    rel?: string;
  };
  onLinkClick?: () => void;
};

const ListingCardImage: FC<ListingCardImageProps> = ({
  src,
  alt,
  photos,
  loading,
  fetchpriority = null,
  link,
  withPlaceholder = true,
  isPopup = false,
  isPreview = false,
  onLinkClick,
}) => {
  const { hasPointer } = useMediaQueries();

  const { ref: intersectionObserverRef, inView } = useInView<HTMLDivElement>({
    threshold: 1,
    trackOnce: true,
  });
  const [allowCarousel, setAllowCarousel] = useState(false);

  const hasPhotos = !!(photos && photos.length > 1);
  const hasNoPointerEvents = isPreview && !link;

  // Enable for mobile, disable for desktop
  const showCarousel = allowCarousel || (!hasPointer && inView && hasPhotos);

  const handleInteraction = useCallback((e) => {
    // Let OnKeyDown run as a click (navigation), don't add carousel
    if ((e && 'code' in e)
      && (e.code === 'Enter' || e.code === 'Space')) {
      return;
    }
    setAllowCarousel(hasPhotos);
  }, [hasPhotos]);

  const renderCarouselAsLink = showCarousel && (
    !isPopup && !isPreview
  );
  const filteredPhotos = useMemo(() => (photos ? filterUnique(photos) : []), [photos]);

  const imgSrcSet = (() => {
    if (isPopup) return popupSrcSet;
    return srcSet;
  })();
  const img = (
    <UniversalLink
      href={link ? link.url : ''}
      aria-label={link?.heading}
      data-test={link?.testMark}
      target={link?.target}
      rel={link?.rel}
      className={getClassNames(styles, 'link', classNames({ hasNoPointerEvents, isPopup }))}
      onClick={onLinkClick}
      onKeyDown={handleInteraction}
      role="button"
    >
      <ImgFromSrcSet
        withIntrinsicSizing
        withFixedLayout
        withRoundedCornersLg={!isPopup}
        withPlaceholder={withPlaceholder}
        src={src}
        alt={alt}
        srcSet={imgSrcSet}
        loading={loading}
        fetchpriority={fetchpriority}
      />
    </UniversalLink>
  );

  const carousel = showCarousel ? (
    <Carousel
      forceEnhanced
      preventEdgeScrolling
      slidesToScroll={1}
      classNameModifier="listingCard"

    >
      {filteredPhotos.map((photo, i) => (
        <Carousel.Item
          key={urlToKey(photo.url)}
          classNameModifier="listingCard"
        >
          <ImgFromSrcSet
            withIntrinsicSizing
            withFixedLayout
            withRoundedCornersLg={false}
            withPlaceholder={i !== 0}
            src={photo.url}
            alt={photo.description}
            srcSet={srcSet}
            loading={i === 0 ? loading : 'lazy'}
            fetchpriority={i === 0 ? fetchpriority : null}
          />
        </Carousel.Item>
      ))}
    </Carousel>
  ) : null;

  return (
    <div
      className={getClassNames(styles, 'root', classNames({ isPopup }))}
      onMouseEnter={handleInteraction}
      ref={intersectionObserverRef}
    >
      {img}
      {renderCarouselAsLink && (
        <UniversalLink
          href={link ? link?.url : ''}
          aria-label={link?.heading}
          data-test={link?.testMark}
          className={styles.link}
          onClick={onLinkClick}
        >
          {carousel}
        </UniversalLink>
      )}
      {isPreview && carousel}
    </div>
  );
};

export default ListingCardImage;
